import { state, resetDraft } from "../store";
import { setRoot, showError } from "../render";
import { createReservation, rescheduleMyReservation } from "../api";

export function renderStep5() {
  const d = state.draft;
  const isReschedule = state.reschedule !== null;

  // Phase 1.5.4 拡張：reschedule モード時は確認画面の構成と CTA を変える
  if (isReschedule) {
    return renderRescheduleConfirm();
  }

  setRoot(`
    <header class="text-center pb-3 border-b">
      <h1 class="font-bold text-lg">✅ 確認</h1>
      <p class="text-xs text-gray-500 mt-1">Step 5 / 5</p>
    </header>

    <div class="mt-4 bg-gray-50 rounded p-3 text-sm leading-7">
      <div><b>📅 日時：</b>${formatJpDate(d.reservation_date!)} ${d.reservation_time!}〜（30分）</div>
      <div><b>👤 名前：</b>${escapeHtml(d.customer_name)}</div>
      <div><b>📹 方法：</b>${d.contact_method === "zoom" ? "オンライン面談" : "LINE電話"}</div>
      <div><b>💼 業種：</b>${d.business_type ?? "—"}</div>
      <div><b>💬 内容：</b>${escapeHtml(d.consultation_topic)}</div>
    </div>

    <div class="mt-4 bg-yellow-50 text-yellow-800 rounded p-3 text-xs leading-6">
      ⏰ 24時間前と1時間前に自動リマインドが届きます
    </div>

    <div class="mt-auto pt-4 flex gap-2">
      <button id="back-btn" class="flex-1 bg-gray-300 text-gray-800 py-3 rounded">戻る</button>
      <button id="confirm-btn" class="flex-[2] bg-confirm text-white py-3 rounded font-bold">予約確定</button>
    </div>
  `);

  document.getElementById("back-btn")!.addEventListener("click", async () => {
    const { renderStep4 } = await import("./step4Topic");
    renderStep4();
  });

  document.getElementById("confirm-btn")!.addEventListener("click", async () => {
    const btn = document.getElementById("confirm-btn") as HTMLButtonElement;
    btn.disabled = true;
    btn.textContent = "送信中...";
    btn.className = "flex-[2] bg-gray-400 text-white py-3 rounded font-bold";

    try {
      const result = await createReservation({
        reservation_date: d.reservation_date!,
        reservation_time: d.reservation_time!,
        customer_name: d.customer_name,
        contact_method: d.contact_method!,
        business_type: d.business_type!,
        consultation_topic: d.consultation_topic,
      } as any);

      if ("error" in result) {
        if (result.error === "RESERVATION_CONFLICT") {
          showError(result.message);
          // Step 2 に戻して再選択
          const { renderStep2 } = await import("./step2Time");
          renderStep2();
          return;
        }
        showError(result.message ?? "送信エラー");
        btn.disabled = false;
        btn.textContent = "予約確定";
        btn.className = "flex-[2] bg-confirm text-white py-3 rounded font-bold";
        return;
      }

      const { renderStepDone } = await import("./stepDone");
      const date = d.reservation_date!;
      const time = d.reservation_time!;
      const method = d.contact_method!;
      resetDraft();
      renderStepDone({ date, time, method });
    } catch (e: any) {
      showError(e?.message ?? "通信エラー");
      btn.disabled = false;
      btn.textContent = "予約確定";
      btn.className = "flex-[2] bg-confirm text-white py-3 rounded font-bold";
    }
  });
}

/**
 * Phase 1.5.4 拡張：日時変更（reschedule）の確認画面。
 * 変更前 / 変更後 を並べて表示し、確定ボタンで rescheduleMyReservation を呼ぶ。
 */
function renderRescheduleConfirm(): void {
  const d = state.draft;
  const old = state.reschedule!;
  const contactLabel =
    old.contact_method === "zoom" ? "オンライン面談" : "LINE電話";

  setRoot(`
    <header class="text-center pb-3 border-b">
      <h1 class="font-bold text-lg">🔄 日時変更の確認</h1>
      <p class="text-xs text-gray-500 mt-1">最終確認</p>
    </header>

    <div class="mt-4 bg-gray-50 rounded-lg p-3 text-sm leading-7">
      <div class="text-gray-500 text-xs mb-1">📌 変更前の予約</div>
      <div class="text-gray-700">
        ${formatJpDate(old.reservation_date)} ${old.reservation_time}
      </div>

      <div class="my-2 border-t border-dashed border-gray-300"></div>

      <div class="text-gray-500 text-xs mb-1">✅ 変更後の予約</div>
      <div class="font-bold text-emerald-700 text-base">
        ${formatJpDate(d.reservation_date!)} ${d.reservation_time!}〜（${old.duration_minutes}分）
      </div>

      <div class="mt-3 text-xs text-gray-600">
        連絡方法：${escapeHtml(contactLabel)}
        ${old.business_type ? `／業種：${escapeHtml(old.business_type)}` : ""}
      </div>
    </div>

    <div class="mt-4 bg-amber-50 border border-amber-200 text-amber-800 rounded p-3 text-xs leading-6">
      ⚠️ 確定すると、変更前の予約は自動的にキャンセルされます。<br>
      変更後の日時で改めてリマインドが届きます。
    </div>

    <div class="mt-auto pt-4 flex gap-2">
      <button id="back-btn" class="flex-1 bg-gray-300 text-gray-800 py-3 rounded">戻る</button>
      <button id="confirm-btn" class="flex-[2] bg-emerald-600 text-white py-3 rounded font-bold">変更を確定する</button>
    </div>
  `);

  document.getElementById("back-btn")!.addEventListener("click", async () => {
    const { renderStep2 } = await import("./step2Time");
    renderStep2();
  });

  document.getElementById("confirm-btn")!.addEventListener("click", async () => {
    const btn = document.getElementById("confirm-btn") as HTMLButtonElement;
    btn.disabled = true;
    btn.textContent = "送信中...";
    btn.className = "flex-[2] bg-gray-400 text-white py-3 rounded font-bold";

    try {
      const result = await rescheduleMyReservation(
        old.id,
        d.reservation_date!,
        d.reservation_time!
      );

      const oldDate = old.reservation_date;
      const oldTime = old.reservation_time;
      const newDate = d.reservation_date!;
      const newTime = d.reservation_time!;
      const method = old.contact_method;
      const newMeetUrl = result.results.new_meet_url;
      resetDraft();
      const { renderRescheduleDone } = await import("../views/rescheduleDone");
      renderRescheduleDone({
        oldDate,
        oldTime,
        newDate,
        newTime,
        contact_method: method,
        meetUrl: newMeetUrl,
      });
    } catch (e: any) {
      showError(e?.message ?? "通信エラー");
      btn.disabled = false;
      btn.textContent = "変更を確定する";
      btn.className = "flex-[2] bg-emerald-600 text-white py-3 rounded font-bold";
    }
  });
}

function formatJpDate(date: string): string {
  const [, m, d] = date.split("-").map(Number);
  const dow = ["日","月","火","水","木","金","土"][new Date(date).getDay()];
  return `${m}/${d}(${dow})`;
}

function escapeHtml(s: string): string {
  return s.replace(/[&<>"']/g, (c) => ({
    "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;"
  }[c]!));
}
